/**
 * Request and response schemas for the patient-chart surface: patient demographics, problem list,
 * allergies, medications, immunizations, and vitals (`docs/roadmap.md` Phase 3, `emr-po BACKLOG.md`
 * C2/C3/C3a).
 *
 * Same reasoning as `authRequests.ts`, restated because it matters just as much here: these live in
 * `shared/` so the server validates against the exact schema the web app infers its types from, every
 * object schema is strict so an unrecognised key is a rejection rather than a silently ignored extra,
 * and no server-owned value (an id, `orgId`, a timestamp) appears in any request schema — the route
 * layer supplies those from the session and the URL, never from the body.
 *
 * Two value sets are open-but-checked rather than a Postgres enum, matching `orgs.tier`'s own
 * reasoning (`db/schema/platform.ts`) and `db/schema/clinical.ts`'s comments on `administrativeSex`
 * and `vitals.vitalType`: a text column plus application-boundary validation means adding a value is a
 * one-line change here, not a migration. `ADMINISTRATIVE_SEX_VALUES` mirrors FHIR's
 * `AdministrativeGender` value set on purpose — this project already commits to FHIR/USCDI-aligned
 * vocabulary where one exists (`confidentiality.ts`'s own note on DS4P). `genderIdentity`, by
 * contrast, is left as bounded free text: USCDI v3's actual Gender Identity value set is SNOMED-coded
 * and richer than anything reasonable to invent here, so wiring the real value set is deferred rather
 * than guessed at — the same "coding is real future work, free text is today's honest answer" choice
 * `clinical.ts` already makes for `problems.icd10Code` and `allergies.reaction`/`severity`.
 */

import { z } from 'zod';

import { RESTRICTION_REASONS } from './confidentiality.ts';
import { CLINICAL_ERROR_CODES } from './encounterRequests.ts';

// --- Shared bounds and primitives -------------------------------------------------------------------

export const MAX_MRN_LENGTH = 64;
export const MAX_NAME_LENGTH = 200;
export const MAX_CONTACT_LENGTH = 254;
export const MAX_ADDRESS_LINE_LENGTH = 200;
export const MAX_CITY_LENGTH = 100;
/** US state/territory abbreviations are two letters, but this also carries non-US practices. */
export const MAX_STATE_LENGTH = 50;
export const MAX_POSTAL_CODE_LENGTH = 20;
export const MAX_FREE_TEXT_LENGTH = 500;
/** Payer name, member id, group number, plan name — kiosk check-in insurance fields (`BACKLOG.md`
 * C7). Same bound as `MAX_NAME_LENGTH`'s sibling fields; none of these has a fixed real-world format
 * (a payer's own member-id scheme varies by plan), so this is a generous cap, not a validated shape. */
export const MAX_INSURANCE_FIELD_LENGTH = 100;
export const MAX_CODE_LENGTH = 20;
export const MAX_LOT_NUMBER_LENGTH = 100;
export const MAX_VITAL_VALUE_LENGTH = 50;
export const MAX_UNIT_LENGTH = 20;
export const MAX_JUSTIFICATION_LENGTH = 2000;

const trimmed = (max: number) => z.string().trim().min(1).max(max);
const optionalTrimmed = (max: number) => trimmed(max).optional();

/** A calendar date, `YYYY-MM-DD` — matches `db/schema/columns.ts`'s `calendarDate` storage exactly. */
export const calendarDateSchema = z.iso.date();

/** A restriction reason from the closed confidentiality vocabulary (`confidentiality.ts`). */
export const restrictionReasonSchema = z.enum(RESTRICTION_REASONS);

/**
 * The label a caller may assign to a record it is creating or updating. Absent means "leave it
 * unrestricted" on create; the service layer is what turns this into the stored `text[]` via
 * `labelOf`, which also de-duplicates and drops anything not in the closed vocabulary.
 */
export const confidentialityReasonsInputSchema = z.array(restrictionReasonSchema).default([]);

/**
 * USCDI v1 "Sex" / FHIR `AdministrativeGender`. Small and stable enough to close as an enum, unlike
 * `genderIdentity` above.
 */
export const ADMINISTRATIVE_SEX_VALUES = ['male', 'female', 'other', 'unknown'] as const;
export type AdministrativeSex = (typeof ADMINISTRATIVE_SEX_VALUES)[number];
export const administrativeSexSchema = z.enum(ADMINISTRATIVE_SEX_VALUES);

/**
 * Vitals this build knows how to record. Open-but-checked, matching `vitals.vitalType`'s own comment:
 * extend by adding a string here, no migration required. Values follow LOINC's common ordering
 * (vitals panel) rather than an invented order.
 */
export const VITAL_TYPES = [
  'heart_rate',
  'respiratory_rate',
  'blood_pressure_systolic',
  'blood_pressure_diastolic',
  'body_temperature',
  'oxygen_saturation',
  'height',
  'weight',
  'bmi',
  'pain_score',
] as const;
export type VitalType = (typeof VITAL_TYPES)[number];
export const vitalTypeSchema = z.enum(VITAL_TYPES);

/** A bounded `limit` for a list endpoint. No cursor yet — see the server report for why. */
export const MAX_LIST_LIMIT = 500;
export const DEFAULT_LIST_LIMIT = 100;
export const listLimitSchema = z.coerce
  .number()
  .int()
  .min(1)
  .max(MAX_LIST_LIMIT)
  .default(DEFAULT_LIST_LIMIT);

// --- Patients ----------------------------------------------------------------------------------------

/**
 * Insurance coverage, kept as flat primary/secondary columns directly on `patients` rather than a
 * separate normalized table (`BACKLOG.md` C7 / `emr-po` A16). C2's patient-demographics table had no
 * insurance modeling at all before the kiosk check-in surface needed one — the brief that produced
 * this addition assumed C2 already covered it, and it did not. A fully normalized
 * `patient_insurance_policies` table (arbitrary rank count, effective/termination dates, subscriber
 * relationship) is the more complete design and is real follow-up work; this is the minimum that is
 * still genuinely *structured* (typed columns the kiosk and staff both write through the same
 * `updatePatient`/`getPatientById` functions), which is the actual bar C7's brief sets — "not a
 * scanned PDF" — not full normalization. Two ranks (primary/secondary) covers the overwhelming
 * majority of real coverage; a patient with a third payer is the rare case this v1 does not model.
 */
const insurancePolicyRequestFields = {
  primaryInsurancePayerName: optionalTrimmed(MAX_INSURANCE_FIELD_LENGTH),
  primaryInsuranceMemberId: optionalTrimmed(MAX_INSURANCE_FIELD_LENGTH),
  primaryInsuranceGroupNumber: optionalTrimmed(MAX_INSURANCE_FIELD_LENGTH),
  primaryInsurancePlanName: optionalTrimmed(MAX_INSURANCE_FIELD_LENGTH),
  secondaryInsurancePayerName: optionalTrimmed(MAX_INSURANCE_FIELD_LENGTH),
  secondaryInsuranceMemberId: optionalTrimmed(MAX_INSURANCE_FIELD_LENGTH),
  secondaryInsuranceGroupNumber: optionalTrimmed(MAX_INSURANCE_FIELD_LENGTH),
  secondaryInsurancePlanName: optionalTrimmed(MAX_INSURANCE_FIELD_LENGTH),
} as const;

export const createPatientRequestSchema = z.strictObject({
  mrn: trimmed(MAX_MRN_LENGTH),
  firstName: trimmed(MAX_NAME_LENGTH),
  lastName: trimmed(MAX_NAME_LENGTH),
  dateOfBirth: calendarDateSchema,
  administrativeSex: administrativeSexSchema,
  genderIdentity: optionalTrimmed(MAX_NAME_LENGTH),
  phone: optionalTrimmed(MAX_CONTACT_LENGTH),
  email: z.email().max(MAX_CONTACT_LENGTH).optional(),
  addressLine1: optionalTrimmed(MAX_ADDRESS_LINE_LENGTH),
  addressLine2: optionalTrimmed(MAX_ADDRESS_LINE_LENGTH),
  city: optionalTrimmed(MAX_CITY_LENGTH),
  state: optionalTrimmed(MAX_STATE_LENGTH),
  postalCode: optionalTrimmed(MAX_POSTAL_CODE_LENGTH),
  confidentialityReasons: confidentialityReasonsInputSchema,
  ...insurancePolicyRequestFields,
});
export type CreatePatientRequest = z.infer<typeof createPatientRequestSchema>;

/**
 * Demographics correction. Every field optional — a caller sends only what changed — but `mrn` is
 * still here: `patients.ts`'s own comment is explicit that an MRN can be reassigned or corrected,
 * unlike the primary key.
 */
export const updatePatientRequestSchema = z.strictObject({
  mrn: trimmed(MAX_MRN_LENGTH).optional(),
  firstName: trimmed(MAX_NAME_LENGTH).optional(),
  lastName: trimmed(MAX_NAME_LENGTH).optional(),
  dateOfBirth: calendarDateSchema.optional(),
  administrativeSex: administrativeSexSchema.optional(),
  genderIdentity: optionalTrimmed(MAX_NAME_LENGTH),
  phone: optionalTrimmed(MAX_CONTACT_LENGTH),
  email: z.email().max(MAX_CONTACT_LENGTH).optional(),
  addressLine1: optionalTrimmed(MAX_ADDRESS_LINE_LENGTH),
  addressLine2: optionalTrimmed(MAX_ADDRESS_LINE_LENGTH),
  city: optionalTrimmed(MAX_CITY_LENGTH),
  state: optionalTrimmed(MAX_STATE_LENGTH),
  postalCode: optionalTrimmed(MAX_POSTAL_CODE_LENGTH),
  confidentialityReasons: z.array(restrictionReasonSchema).optional(),
  ...insurancePolicyRequestFields,
});
export type UpdatePatientRequest = z.infer<typeof updatePatientRequestSchema>;

/**
 * The subset of `updatePatientRequestSchema` a kiosk check-in token may write (`BACKLOG.md` C7).
 * Deliberately excludes `mrn` and `confidentialityReasons`: an MRN correction and a confidentiality
 * restriction are both staff-administrative decisions a patient at a tablet must never make
 * unilaterally — `mrn` because it is a billing/record-linkage identifier, not a demographic fact the
 * patient is the authority on, and `confidentialityReasons` because it is *about* who else may see
 * this chart, which is exactly the kind of access-control decision `docs/roadmap.md` never lets the
 * subject of a record set for themselves. Every other field here is squarely "verify/correct data
 * already on file," which is the entire point of the kiosk flow (`BACKLOG.md` C7's own framing).
 */
export const kioskUpdatePatientRequestSchema = updatePatientRequestSchema.omit({
  mrn: true,
  confidentialityReasons: true,
});
export type KioskUpdatePatientRequest = z.infer<typeof kioskUpdatePatientRequestSchema>;

/** How many distinct patients a single batch-by-id lookup (`patientSearchQuerySchema`'s `ids`) may
 * request at once — `BACKLOG.md` K5. Bounded the same way `listLimitSchema` bounds a page, for the
 * same reason: an unbounded `IN (...)` list is still a query-shape a caller controls the size of. A
 * week's worth of distinct scheduled patients is nowhere near this — sized generously, not tightly. */
export const MAX_BATCH_IDS = 200;

export const patientSearchQuerySchema = z.object({
  /** Exact match against the unique index — never partial, unlike every other field here. */
  mrn: trimmed(MAX_MRN_LENGTH).optional(),
  /** Matched as a `last_name` prefix, using `patients_org_last_name_dob_idx`. */
  name: trimmed(MAX_NAME_LENGTH).optional(),
  /** Matched as a `first_name` prefix, using `patients_org_first_name_idx`. */
  firstName: trimmed(MAX_NAME_LENGTH).optional(),
  /** Exact match — a date of birth has no meaningful "prefix". */
  dateOfBirth: calendarDateSchema.optional(),
  /** A batch-by-id lookup (`BACKLOG.md` K5) — composes as one more AND'd condition against the same
   * org-scoped query every other field here already uses, matching this schema's own existing
   * "every field is independently optional and AND'd together" design rather than carving out a
   * separate mutually-exclusive request shape. In practice a caller uses this alone (a batch lookup
   * and a name/mrn search are different use cases), but nothing here needs to enforce that — the
   * same "nothing requires only one field at a time" property `searchPatients`'s own header already
   * states for the other filters. */
  ids: z.array(z.uuid()).min(1).max(MAX_BATCH_IDS).optional(),
  limit: listLimitSchema,
});
export type PatientSearchQuery = z.infer<typeof patientSearchQuerySchema>;

export const patientResponseSchema = z.strictObject({
  id: z.uuid(),
  mrn: z.string(),
  firstName: z.string(),
  lastName: z.string(),
  dateOfBirth: z.string(),
  administrativeSex: z.string(),
  genderIdentity: z.string().nullable(),
  phone: z.string().nullable(),
  email: z.string().nullable(),
  addressLine1: z.string().nullable(),
  addressLine2: z.string().nullable(),
  city: z.string().nullable(),
  state: z.string().nullable(),
  postalCode: z.string().nullable(),
  source: z.string(),
  confidentialityReasons: z.array(z.string()),
  primaryInsurancePayerName: z.string().nullable(),
  primaryInsuranceMemberId: z.string().nullable(),
  primaryInsuranceGroupNumber: z.string().nullable(),
  primaryInsurancePlanName: z.string().nullable(),
  secondaryInsurancePayerName: z.string().nullable(),
  secondaryInsuranceMemberId: z.string().nullable(),
  secondaryInsuranceGroupNumber: z.string().nullable(),
  secondaryInsurancePlanName: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type PatientResponse = z.infer<typeof patientResponseSchema>;

/**
 * The kiosk-facing projection of `patientResponseSchema` (`BACKLOG.md` C7) — everything a patient
 * reviewing their own demographics at a tablet needs to see, minus two fields that are staff/org
 * metadata rather than the patient's own data: `confidentialityReasons` (who else may see this
 * chart — an access-control fact about the record, not a demographic fact) and `source` (where the
 * record originated — an internal provenance field with no reason to reach an unauthenticated
 * surface). Minimum-necessary, the same posture `careTeam.ts`'s `administrative` access scope already
 * takes for front-desk/billing staff, applied one step further for an even less-trusted caller.
 */
export const kioskPatientResponseSchema = patientResponseSchema.omit({
  confidentialityReasons: true,
  source: true,
});
export type KioskPatientResponse = z.infer<typeof kioskPatientResponseSchema>;

// --- Patient duplicate matching (BACKLOG.md C2a) --------------------------------------------------

/**
 * The duplicate-match review queue's own status vocabulary. `pending` is the only status
 * `patientDuplicatesService.ts` ever assigns on generation; a caller may only resolve a pending
 * candidate to one of the two terminal states — nothing here routes a candidate back to `pending`.
 */
export const PATIENT_DUPLICATE_STATUSES = [
  'pending',
  'confirmed_duplicate',
  'confirmed_distinct',
] as const;
export type PatientDuplicateStatus = (typeof PATIENT_DUPLICATE_STATUSES)[number];

/**
 * A staff decision on one candidate pair. Deliberately narrower than the full
 * `PATIENT_DUPLICATE_STATUSES` union — `pending` is never a valid *target* of a resolve request,
 * only the state a newly generated candidate starts in.
 */
export const resolvePatientDuplicateRequestSchema = z.strictObject({
  status: z.enum(['confirmed_duplicate', 'confirmed_distinct']),
});
export type ResolvePatientDuplicateRequest = z.infer<typeof resolvePatientDuplicateRequestSchema>;

/**
 * One candidate pair. `reasons` mirrors `@emr/shared`'s own `MatchReason` values (`patientMatching.ts`)
 * as plain strings, matching `confidentialityReasons`' own open-but-checked response shape above — the
 * client renders each one through a lookup table keyed by these strings, rather than this schema
 * importing the closed union directly.
 *
 * Deliberately carries only ids, not either patient's own demographics — `GET /api/patients?ids=`
 * (`BACKLOG.md` K5) is the existing batch-lookup surface a caller resolves both records through,
 * rather than this response duplicating patient fields a second time.
 */
export const patientDuplicateCandidateResponseSchema = z.strictObject({
  id: z.uuid(),
  patientId: z.uuid(),
  candidatePatientId: z.uuid(),
  score: z.number().int().min(0).max(100),
  reasons: z.array(z.string()),
  // A plain, open string on the way out — matching `credentialResponseSchema.status`'s own
  // convention — not `z.enum(PATIENT_DUPLICATE_STATUSES)`: the closed union governs what a *request*
  // may set (`resolvePatientDuplicateRequestSchema`), the response just reports whatever is stored.
  status: z.string(),
  resolvedByUserId: z.uuid().nullable(),
  resolvedAt: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type PatientDuplicateCandidateResponse = z.infer<
  typeof patientDuplicateCandidateResponseSchema
>;

// --- Problems ------------------------------------------------------------------------------------------

export const createProblemRequestSchema = z.strictObject({
  description: trimmed(MAX_FREE_TEXT_LENGTH),
  icd10Code: optionalTrimmed(MAX_CODE_LENGTH),
  status: optionalTrimmed(MAX_NAME_LENGTH),
  onsetDate: calendarDateSchema.optional(),
  confidentialityReasons: confidentialityReasonsInputSchema,
});
export type CreateProblemRequest = z.infer<typeof createProblemRequestSchema>;

export const updateProblemRequestSchema = z.strictObject({
  description: trimmed(MAX_FREE_TEXT_LENGTH).optional(),
  icd10Code: optionalTrimmed(MAX_CODE_LENGTH),
  status: optionalTrimmed(MAX_NAME_LENGTH),
  onsetDate: calendarDateSchema.optional(),
  confidentialityReasons: z.array(restrictionReasonSchema).optional(),
});
export type UpdateProblemRequest = z.infer<typeof updateProblemRequestSchema>;

export const problemResponseSchema = z.strictObject({
  id: z.uuid(),
  patientId: z.uuid(),
  description: z.string(),
  icd10Code: z.string().nullable(),
  status: z.string(),
  onsetDate: z.string().nullable(),
  confidentialityReasons: z.array(z.string()),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type ProblemResponse = z.infer<typeof problemResponseSchema>;

// --- Allergies -----------------------------------------------------------------------------------------

export const createAllergyRequestSchema = z.strictObject({
  substance: trimmed(MAX_NAME_LENGTH),
  reaction: optionalTrimmed(MAX_FREE_TEXT_LENGTH),
  severity: optionalTrimmed(MAX_NAME_LENGTH),
  status: optionalTrimmed(MAX_NAME_LENGTH),
  confidentialityReasons: confidentialityReasonsInputSchema,
});
export type CreateAllergyRequest = z.infer<typeof createAllergyRequestSchema>;

export const updateAllergyRequestSchema = z.strictObject({
  substance: trimmed(MAX_NAME_LENGTH).optional(),
  reaction: optionalTrimmed(MAX_FREE_TEXT_LENGTH),
  severity: optionalTrimmed(MAX_NAME_LENGTH),
  status: optionalTrimmed(MAX_NAME_LENGTH),
  confidentialityReasons: z.array(restrictionReasonSchema).optional(),
});
export type UpdateAllergyRequest = z.infer<typeof updateAllergyRequestSchema>;

export const allergyResponseSchema = z.strictObject({
  id: z.uuid(),
  patientId: z.uuid(),
  substance: z.string(),
  reaction: z.string().nullable(),
  severity: z.string().nullable(),
  status: z.string(),
  confidentialityReasons: z.array(z.string()),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type AllergyResponse = z.infer<typeof allergyResponseSchema>;

// --- Medications ---------------------------------------------------------------------------------------

/**
 * Recording only — never e-prescribing. No `externalRxId` anywhere in this file: `medications.ts`'s
 * own comment is explicit that it is set only once a real Surescripts integration exists, and it must
 * never arrive from a request body in the meantime (`docs/roadmap.md` C3a).
 */
export const createMedicationRequestSchema = z.strictObject({
  name: trimmed(MAX_NAME_LENGTH),
  dose: optionalTrimmed(MAX_NAME_LENGTH),
  route: optionalTrimmed(MAX_NAME_LENGTH),
  frequency: optionalTrimmed(MAX_NAME_LENGTH),
  status: optionalTrimmed(MAX_NAME_LENGTH),
  startDate: calendarDateSchema.optional(),
  endDate: calendarDateSchema.optional(),
  /** The prescribing clinician. Not validated against `users` here — see the server report. */
  prescriberId: z.uuid().optional(),
  confidentialityReasons: confidentialityReasonsInputSchema,
});
export type CreateMedicationRequest = z.infer<typeof createMedicationRequestSchema>;

export const updateMedicationRequestSchema = z.strictObject({
  name: trimmed(MAX_NAME_LENGTH).optional(),
  dose: optionalTrimmed(MAX_NAME_LENGTH),
  route: optionalTrimmed(MAX_NAME_LENGTH),
  frequency: optionalTrimmed(MAX_NAME_LENGTH),
  status: optionalTrimmed(MAX_NAME_LENGTH),
  startDate: calendarDateSchema.optional(),
  endDate: calendarDateSchema.optional(),
  prescriberId: z.uuid().optional(),
  confidentialityReasons: z.array(restrictionReasonSchema).optional(),
});
export type UpdateMedicationRequest = z.infer<typeof updateMedicationRequestSchema>;

export const medicationResponseSchema = z.strictObject({
  id: z.uuid(),
  patientId: z.uuid(),
  prescriberId: z.uuid().nullable(),
  name: z.string(),
  dose: z.string().nullable(),
  route: z.string().nullable(),
  frequency: z.string().nullable(),
  status: z.string(),
  startDate: z.string().nullable(),
  endDate: z.string().nullable(),
  externalRxId: z.string().nullable(),
  confidentialityReasons: z.array(z.string()),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type MedicationResponse = z.infer<typeof medicationResponseSchema>;

// --- Immunizations -------------------------------------------------------------------------------------

export const createImmunizationRequestSchema = z.strictObject({
  vaccine: trimmed(MAX_NAME_LENGTH),
  administeredDate: calendarDateSchema,
  lotNumber: optionalTrimmed(MAX_LOT_NUMBER_LENGTH),
  confidentialityReasons: confidentialityReasonsInputSchema,
});
export type CreateImmunizationRequest = z.infer<typeof createImmunizationRequestSchema>;

export const updateImmunizationRequestSchema = z.strictObject({
  vaccine: trimmed(MAX_NAME_LENGTH).optional(),
  administeredDate: calendarDateSchema.optional(),
  lotNumber: optionalTrimmed(MAX_LOT_NUMBER_LENGTH),
  confidentialityReasons: z.array(restrictionReasonSchema).optional(),
});
export type UpdateImmunizationRequest = z.infer<typeof updateImmunizationRequestSchema>;

export const immunizationResponseSchema = z.strictObject({
  id: z.uuid(),
  patientId: z.uuid(),
  vaccine: z.string(),
  administeredDate: z.string(),
  lotNumber: z.string().nullable(),
  confidentialityReasons: z.array(z.string()),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type ImmunizationResponse = z.infer<typeof immunizationResponseSchema>;

// --- Vitals --------------------------------------------------------------------------------------------

/**
 * Create only. `vitals` carries no `deletedAt` (`db/schema/clinical.ts`) — a recorded observation is
 * immutable, matching an audit-log entry rather than an editable chart note, so there is deliberately
 * no update or delete schema here.
 */
export const createVitalRequestSchema = z.strictObject({
  vitalType: vitalTypeSchema,
  value: trimmed(MAX_VITAL_VALUE_LENGTH),
  unit: trimmed(MAX_UNIT_LENGTH),
  recordedAt: z.iso.datetime({ offset: true }),
  encounterId: z.uuid().optional(),
  confidentialityReasons: confidentialityReasonsInputSchema,
});
export type CreateVitalRequest = z.infer<typeof createVitalRequestSchema>;

export const vitalResponseSchema = z.strictObject({
  id: z.uuid(),
  patientId: z.uuid(),
  encounterId: z.uuid().nullable(),
  vitalType: z.string(),
  value: z.string(),
  unit: z.string(),
  recordedAt: z.string(),
  confidentialityReasons: z.array(z.string()),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type VitalResponse = z.infer<typeof vitalResponseSchema>;

// --- Chart export (C22) ---------------------------------------------------------------------------

/**
 * Every record type the staff-facing chart-export surface (`BACKLOG.md` C22) can include. Matches
 * `PatientChartPage.tsx`'s own tab set exactly — problems, allergies, medications, immunizations,
 * vitals, labs, plus encounters and notes split into their own two entries rather than one combined
 * `encountersNotes` the way the UI tab groups them, since `chartExportService.ts` fetches and audits
 * each independently. Deliberately excludes the `M5`-`M22` specialty-module data C22's own backlog
 * text names as a "whichever your org is entitled to" follow-on — not built in this pass.
 */
export const CHART_EXPORT_RECORD_TYPES = [
  'problems',
  'allergies',
  'medications',
  'immunizations',
  'vitals',
  'labs',
  'encounters',
  'notes',
] as const;
export type ChartExportRecordType = (typeof CHART_EXPORT_RECORD_TYPES)[number];
export const chartExportRecordTypeSchema = z.enum(CHART_EXPORT_RECORD_TYPES);

/**
 * A staff-initiated export request: which record types, and an optional inclusive date range applied
 * to each type's own natural clinical date (`chartExportService.ts`'s own comment names which field
 * that is, per type — a fallback to `createdAt` for a type with no better one, like `allergies`).
 * Omitting both dates exports every record of the selected types with no bound. Duplicate entries in
 * `recordTypes` are harmless (the service de-duplicates before building sections) so this schema does
 * not reject them — a caller has no reason to send them, but nothing about receiving one is unsafe.
 */
export const chartExportRequestSchema = z
  .strictObject({
    recordTypes: z.array(chartExportRecordTypeSchema).min(1).max(CHART_EXPORT_RECORD_TYPES.length),
    startDate: calendarDateSchema.optional(),
    endDate: calendarDateSchema.optional(),
  })
  .refine(
    (value) =>
      value.startDate === undefined ||
      value.endDate === undefined ||
      value.startDate <= value.endDate,
    { error: 'startDate must not be after endDate', path: ['endDate'] },
  );
export type ChartExportRequest = z.infer<typeof chartExportRequestSchema>;

// --- Failure vocabulary ----------------------------------------------------------------------------------

/**
 * Extends `encounterRequests.ts`'s `CLINICAL_ERROR_CODES` rather than declaring a second, colliding
 * union — that file's codes (`chart_access_denied`, `forbidden`, `not_found`, `invalid_request`,
 * `audit_unavailable`) already name exactly the outcomes this surface also needs, since both are
 * routes behind the same `decideChartAccess`/audit chokepoints. `mrn_taken` is the one outcome unique
 * to this surface — patient creation is the only write here with its own uniqueness constraint.
 * `already_resolved` is the duplicate-review queue's own equivalent: a second attempt to resolve a
 * candidate pair someone else already decided (`patientDuplicatesService.ts`'s
 * `resolveDuplicateCandidate`), rather than silently overwriting their decision.
 */
export const PATIENT_ERROR_CODES = [
  ...CLINICAL_ERROR_CODES,
  'mrn_taken',
  'already_resolved',
] as const;
export type PatientErrorCode = (typeof PATIENT_ERROR_CODES)[number];
